/**
 * ModelBadge Component
 *
 * Displays a compact badge showing model provider logo and model name.
 * Used to display judge models in the LLM Judge Scores module.
 */

import React from 'react';
import { getModelLogo } from '../../../utils/logoMapping';
import { formatModelDisplayName } from '../../../utils/modelDisplayName';

export interface ModelBadgeProps {
  provider: string;
  model: string;
  displayName?: string;
  size?: 'small' | 'medium';
}

export const ModelBadge: React.FC<ModelBadgeProps> = ({
  provider,
  model,
  displayName: providedDisplayName,
  size = 'small'
}) => {
  const displayName = providedDisplayName || formatModelDisplayName(model);
  const logoSize = size === 'small' ? 'w-5 h-5' : 'w-6 h-6';
  const textSize = size === 'small' ? 'text-xs' : 'text-sm';
  const padding = size === 'small' ? 'px-2 py-1' : 'px-3 py-1.5';

  return (
    <div
      className={`inline-flex items-center gap-1.5 ${padding} bg-slate-800/60 border border-slate-600/50 rounded-lg ${textSize}`}
      title={`${provider} - ${displayName}`}
    >
      <div className={`${logoSize} bg-slate-700/30 rounded flex items-center justify-center p-0.5 flex-shrink-0`}>
        <img
          src={getModelLogo(model, provider)}
          alt={`${provider} logo`}
          className="w-full h-full object-contain"
        />
      </div>
      <span className="font-medium text-slate-200 truncate">
        {displayName}
      </span>
    </div>
  );
};
